import {
    ApiCartItem,
    ApiContentSection,
    ApiShopperLandingPageLink,
    ApiProductSource,
    ApiSectionType,
    isApiFullLandingPageLink,
    selectVariantWithFallback,
    VLPVariant,
} from '@storefronts/common-types'
import { AbandonmentStorefrontEventData, AjaxShopifyCartItem } from '../types'
import { getCartLinePrice, getVariantPrices } from './priceFormat'
import type { ApiPromotion } from '@storefronts/common-types'
import { VariantWithImage } from '../types'

export function getLandingPageLinksFromProductHistory(
    abandonmentStorefrontData:
        | AbandonmentStorefrontEventData
        | undefined
        | null,
): ApiShopperLandingPageLink[] {
    // TODO: Sort sort? Based on event_type?  Based on order of appearance on the page?
    // TODO: Filter out products that aren't displayed on the page?
    const productHistory = abandonmentStorefrontData?.product_history ?? []
    return productHistory
        .map((product) => {
            return product.landing_page_link
        })
        .filter((link) => !!link) as ApiShopperLandingPageLink[]
}

const apiProductSourceToEventTypes = {
    [ApiProductSource.RECENTLY_VIEWED]: ['ViewedProduct'],
    [ApiProductSource.ADDED_TO_CART]: ['AddToCart', 'InitiateCheckout'],
    [ApiProductSource.PURCHASED]: ['Purchase'],
}

export function getProductHistoryByEventTypes(
    abandonmentStorefrontData:
        | AbandonmentStorefrontEventData
        | undefined
        | null,
    apiProductSource: keyof typeof ApiProductSource | undefined,
) {
    if (!apiProductSource) {
        return []
    }
    const productHistory = abandonmentStorefrontData?.product_history ?? []
    return (productHistory ?? []).filter((product) => {
        return (
            apiProductSourceToEventTypes[apiProductSource]?.includes(
                product.event_type,
            ) && !!product.landing_page_link
        )
    })
}

/**
 * Returns a unique list of ApiShopperLandingPageLinks in the order of their first appearance on the page,
 * followed by the remaining ApiShopperLandingPageLinks from the user's history and the main landingPage's
 * landingPageLinks.
 */
export function getCombinedProductHistoryAndLandingPageLinks(
    abandonmentStorefrontData:
        | AbandonmentStorefrontEventData
        | undefined
        | null,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    sections: ApiContentSection[] = [],
): ApiShopperLandingPageLink[] {
    const landingPageLinksMap = new Map<string, ApiShopperLandingPageLink>()

    function addUniqueLandingPageLinks(
        links: ApiShopperLandingPageLink[] = [],
    ) {
        links.forEach((link) => {
            if (!landingPageLinksMap.has(link.shopifyId)) {
                landingPageLinksMap.set(link.shopifyId, link)
            }
        })
    }

    // add all of the products in the order of appearance on the page.
    // duplicates are filtered out.
    sections.forEach((section) => {
        switch (section.type) {
            case ApiSectionType.PLP:
                addUniqueLandingPageLinks(landingPageLinks)
                break
            case ApiSectionType.PRODUCT_CARD:
                addUniqueLandingPageLinks(
                    landingPageLinks.filter((link) =>
                        section.linkHashes.includes(link.linkHash),
                    ),
                )
                break
            case ApiSectionType.INLINE_PDP:
            case ApiSectionType.REVIEWS_IMAGES:
            case ApiSectionType.REVIEWS_SECTION:
            case ApiSectionType.AI_REVIEWS_SUMMARY:
                addUniqueLandingPageLinks(
                    landingPageLinks.filter(
                        (link) => section.linkHash === link.linkHash,
                    ),
                )
                break
            case ApiSectionType.PERSONALIZED_PLP:
                const productHistory = getProductHistoryByEventTypes(
                    abandonmentStorefrontData,
                    section.productSource,
                )
                    .map(
                        (product: {
                            landing_page_link?: ApiShopperLandingPageLink
                        }) => product.landing_page_link,
                    )
                    .filter((link) => !!link) as ApiShopperLandingPageLink[]
                addUniqueLandingPageLinks(productHistory)
                break
            default:
                break
        }
    })
    // append the user's history and the static landing page links to catch
    // any remaining products.
    // duplicates will be filtered out.
    addUniqueLandingPageLinks(
        getLandingPageLinksFromProductHistory(abandonmentStorefrontData),
    )
    addUniqueLandingPageLinks(landingPageLinks)

    return Array.from(landingPageLinksMap.values())
}

// Functions related to the Abandonment cart recovery and converting AjaxCartItem to VariantWithImage.
export function getDiscountedPrice(
    variant: VLPVariant,
    discount?: ApiPromotion,
    link?: ApiShopperLandingPageLink,
) {
    return getVariantPrices(variant, discount || null, link).price
}

export function resolveCartTrackingVariant(
    link: ApiShopperLandingPageLink,
    selectedVariantId: string | null,
): VariantWithImage | undefined {
    if (!link.variantInfo?.length) return undefined
    const variant = selectVariantWithFallback({
        variants: link.variantInfo,
        selectedVariantId,
        defaultVariantId: link.renderedDefaultVariantId,
    })
    if (!variant) return undefined
    return buildTrackingVariantForCart(link, variant)
}

function buildTrackingVariantForCart(
    link: ApiShopperLandingPageLink,
    variant: VLPVariant,
): VariantWithImage {
    const carouselImage = isApiFullLandingPageLink(link)
        ? link.carouselImages?.[0]
        : undefined
    return {
        shopifyId: variant.shopifyId,
        title: link.title,
        option1: variant.option1 ?? null,
        option2: variant.option2 ?? null,
        option3: variant.option3 ?? null,
        featuredImage: carouselImage ?? null,
        price: variant.price,
        available: variant.available ?? true,
        imageUrl: variant.featuredImage || carouselImage || link.imageUrl,
        link,
        finalPrice: variant.price.price,
        isSub: false,
    }
}

export function findVariantWithImageForApiCartItem(
    item: ApiCartItem,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    discount?: ApiPromotion,
): VariantWithImage | undefined {
    return findVariantWithImageByShopifyIds(
        item.product.shopifyId,
        item.variant.shopifyId ?? '',
        landingPageLinks,
        discount,
    )
}

export function findVariantWithImageForAjaxCartItem(
    item: AjaxShopifyCartItem,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    discount?: ApiPromotion,
): VariantWithImage | undefined {
    return findVariantWithImageByShopifyIds(
        `${item.product_id}`,
        `${item.id}`,
        landingPageLinks,
        discount,
    )
}

export function findVariantWithImageByShopifyIds(
    productId: string,
    variantId: string,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    discount?: ApiPromotion,
) {
    const link = landingPageLinks.find((link) => link.shopifyId === productId)
    const variant = link?.variantInfo?.find((variant) => {
        return variant.shopifyId === variantId
    })
    return link && variant
        ? {
              ...variant,
              imageUrl: variant.featuredImage || link?.imageUrl,
              link,
              finalPrice: getCartLinePrice(link, discount ?? null, variant),
              isSub: false,
          }
        : undefined
}

// eslint-disable-next-line complexity
export function convertAjaxToVariant(
    item: AjaxShopifyCartItem,
    discount?: ApiPromotion,
): VariantWithImage {
    // TBD: If we need to port the discountAllocations logic present for ApiCart/ApiCartItem
    // here. This is for a vanishingly small subset of partners using abandonment storefronts
    // pointing to carts with incompatible cartTokens and/or missing access scopes with
    // list items that are novel to the landing page.
    const prices = [
        // Not sure if we should include the discounted prices provided that the
        // landing page has it's own discount code.
        // !landingPage.discount ? item.final_price : undefined,
        // !landingPage.discount ? item.discounted_price : undefined,
        item.price,
        item.original_price,
    ].filter((item) => item !== undefined)
    const price = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    // TODO: Fetch compare at price form product data.
    const compareAtPrice = maxPrice > price ? maxPrice : null
    const variant = {
        shopifyId: `${item.id}`,
        title: item.variant_title,
        // TODO: Note order of options with values sbased on products/options
        option1:
            item.option1 ??
            item.options?.[0] ??
            item.options_with_values?.[0]?.value,
        option2:
            item.option2 ??
            item.options?.[1] ??
            item.options_with_values?.[1]?.value,
        option3:
            item.option3 ??
            item.options?.[2] ??
            item.options_with_values?.[2]?.value,
        featuredImage: item.featured_image?.url ?? item?.image,
        price: {
            price: price,
            compareAtPrice: compareAtPrice,
        },
    } as VLPVariant
    const variantWithImage = {
        // TODO: get from product data.
        ...variant,
        available: true,
        imageUrl: item.image,
        isSub: !!item.selling_plan_allocation,
        // TODO: get product info?
        link: {
            shopifyId: `${item.product_id}`,
            title: item.product_title,
            // Assumption, if there is an option, there are multiple variants
            // need this to display the variant's title in the cart.
            variantInfo: variant.option1 ? [{}, {}] : [],
        } as ApiShopperLandingPageLink,
    } as VariantWithImage
    // finalPrice should reflect the discounted price.
    const finalPriceCandidates = [
        item.discounted_price,
        item.final_price,
        price,
        getDiscountedPrice(variant, discount),
    ].filter((price) => price !== undefined)
    variantWithImage.finalPrice = Math.min(...finalPriceCandidates)
    return variantWithImage
}
